import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const formatApiErrorDetail = (detail) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join(" ");
  }
  return detail.msg || detail.message || String(detail);
};

const normalizeApiError = (err) => {
  const detail = err?.response?.data?.detail || err?.response?.data || err?.message;
  const error = new Error(formatApiErrorDetail(detail) || "Une erreur est survenue.");
  error.response = err?.response;
  return error;
};

const apiRequest = async (method, path, data) => {
  try {
    const response = await api.request({ method, url: path, data });
    return response.data;
  } catch (err) {
    throw normalizeApiError(err);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const data = await apiRequest("GET", "/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const data = await apiRequest("POST", "/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (userData) => {
    const data = await apiRequest("POST", "/auth/register", userData);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await apiRequest("POST", "/auth/logout", {});
    setUser(false);
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    const data = await apiRequest("PUT", "/auth/profile", profileData);
    setUser((prev) => ({ ...prev, ...data }));
    return data;
  }, []);

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    updateProfile,
  }), [user, loading, login, register, logout, checkAuth, updateProfile]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
